import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const MarketplaceSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const kind = params.get('kind');
  const [state, setState] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setState('failed'); return; }
    try {
      const res = await axios.get(`${API_URL}/marketplace/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') { setState('success'); return; }
      if (res.data.status === 'expired') { setState('failed'); return; }
      if (attempts < 6) {
        setTimeout(() => setAttempts((a) => a + 1), 1500);
      } else {
        setState('failed');
      }
    } catch (e) {
      setState('failed');
    }
  }, [sessionId, attempts]);

  useEffect(() => { document.title = 'Payment — I Need A Social Worker'; }, []);
  useEffect(() => { if (state === 'checking') poll(); }, [poll, state, attempts]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-24 pb-16 px-4">
      <div className="bg-white rounded-2xl border p-10 max-w-md w-full text-center" data-testid="marketplace-success">
        {state === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-secondary mx-auto mb-4 animate-spin" />
            <h1 className="font-serif text-2xl font-bold text-primary">Confirming your payment…</h1>
            <p className="text-gray-500 mt-2">This only takes a moment.</p>
          </>
        )}
        {state === 'success' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
            <h1 className="font-serif text-2xl font-bold text-primary">Payment successful!</h1>
            {kind === 'supervisor' ? (
              <p className="text-gray-600 mt-2">Your supervisor membership is active. Your listing will go live as soon as our team verifies your credentials.</p>
            ) : (
              <p className="text-gray-600 mt-2">Your internship listing is now live. Students can find and apply to it right away.</p>
            )}
            <div className="mt-6 flex flex-col gap-2">
              {kind === 'supervisor' ? (
                <Link to="/supervisor/dashboard"><Button className="w-full bg-primary text-white" data-testid="go-dashboard">Go to My Dashboard</Button></Link>
              ) : (
                <Link to="/internships"><Button className="w-full bg-primary text-white" data-testid="go-internships">View Internships</Button></Link>
              )}
              <Link to="/"><Button variant="outline" className="w-full">Back to Home</Button></Link>
            </div>
          </>
        )}
        {state === 'failed' && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="font-serif text-2xl font-bold text-primary">We couldn't confirm your payment</h1>
            <p className="text-gray-600 mt-2">If you were charged, it will process shortly. Otherwise, please try again.</p>
            <Link to="/" className="block mt-6"><Button variant="outline" className="w-full">Back to Home</Button></Link>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketplaceSuccess;
